import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApi, useIncremental } from "@/lib/cache";
import { LoadState, ListSentinel } from "@/components/LoadState";
import { Input } from "@/components/ui/input";
import { ArtworkPlaceholder } from "@/components/ArtworkPlaceholder";
import { ChevronDown, Search, Play, Pencil } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { EditItemDialog } from "@/components/EditItemDialog";
import { useAuth } from "@/context/AuthContext";
const FILTERS = [
  { k: "all", label: "All" },
  { k: "wip", label: "WIP" },
  { k: "done", label: "Completed" },
  { k: "single", label: "Singles" },
  { k: "project", label: "In Project" },
];

export default function Canciones() {
  const { data: songsData, loading, error, reload: load } = useApi("/songs");
  const songs = songsData || [];
  const [versions, setVersions] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [params] = useSearchParams();
  const [filter, setFilter] = useState(() => params.get("filter") || "all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const { play, playSong } = usePlayer();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const navigate = useNavigate();

  const toggle = async (id) => {
    const next = expanded === id ? null : id;
    setExpanded(next);
    if (next && !versions[id]) {
      const { data } = await api.get(`/songs/${id}/versions`);
      setVersions((v) => ({ ...v, [id]: data }));
    }
  };

  // Exclude released from active songs list; those live in Releases
  const activeSongs = songs.filter((s) => !s.released);
  const filtered = activeSongs.filter((s) => {
    if (q && !s.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (filter === "wip") return s.progress < 100;
    if (filter === "done") return s.progress >= 100;
    if (filter === "single") return !s.project_id;
    if (filter === "project") return !!s.project_id;
    return true;
  });
  const { visible, sentinelRef, hasMore } = useIncremental(filtered);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto animate-in">
      <header className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Songs</h1>
        <p className="text-sm text-zinc-500 mt-1">{activeSongs.length} in total</p>
      </header>

      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search song..."
            data-testid="songs-search" className="bg-black/40 border-white/10 pl-9" />
        </div>
        <div className="flex gap-2 overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0 pb-1"
             data-testid="songs-filter-strip"
             style={{ scrollbarWidth: "none" }}>
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`songs-filter-${f.k}`}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${filter === f.k ? "bg-violet-600 text-white" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading || error ? (
        <LoadState loading={loading} error={error} onRetry={load} testid="songs" />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-lg text-zinc-300 mb-2">No songs yet.</p>
          <p className="text-sm text-zinc-500">Tap + New to create your first.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-white/5 bg-[#0F0F11] overflow-hidden divide-y divide-white/5">
          {visible.map((s) => (
            <div key={s.id} data-testid={`song-row-${s.id}`}>
              <div className="flex items-center gap-3 p-3 md:p-4 hover:bg-white/[0.02]">
                <button onClick={() => toggle(s.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5"
                  data-testid={`song-expand-${s.id}`}>
                  <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${expanded === s.id ? "rotate-180" : ""}`} />
                </button>
                <button onClick={() => navigate(`/songs/${s.id}`)} className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  data-testid={`song-open-${s.id}`}>
                  <ArtworkPlaceholder title={s.name} artworkId={s.artwork_id} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{s.name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="h-1 w-20 md:w-32 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-violet-500" style={{ width: `${s.progress || 0}%` }} />
                      </div>
                      <span className="mono text-[10px] text-zinc-500 tabular">{Math.round(s.progress || 0)}%</span>
                      {s.bpm && <span className="mono text-[10px] text-zinc-500 tabular hidden md:inline">· {s.bpm} BPM</span>}
                    </div>
                  </div>
                </button>
                <button onClick={(e) => { e.stopPropagation(); playSong(s); }} data-testid={`song-quickplay-${s.id}`}
                  title="Play latest version"
                  className="w-9 h-9 rounded-full bg-violet-600/20 hover:bg-violet-600 text-violet-300 hover:text-white flex items-center justify-center transition-colors">
                  <Play className="w-3.5 h-3.5 ml-0.5" />
                </button>
                {isOwner && (
                  <button onClick={() => setEditing(s)} data-testid={`song-edit-${s.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-white hover:bg-white/5">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {expanded === s.id && (
                <div className="px-4 pb-4 bg-black/20">
                  {(versions[s.id] || []).length === 0 ? (
                    <p className="text-xs text-zinc-500 py-2">No versions yet. Open the song to upload audio.</p>
                  ) : (
                    <ul className="space-y-1">
                      {(versions[s.id] || []).map((v) => (
                        <li key={v.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.03]">
                          <button onClick={() => play(s, v)} data-testid={`song-play-${v.id}`}
                            className="w-8 h-8 rounded-full bg-violet-600/20 hover:bg-violet-600 text-violet-300 hover:text-white flex items-center justify-center transition-colors">
                            <Play className="w-3.5 h-3.5 ml-0.5" />
                          </button>
                          <span className="mono text-xs text-violet-300">{v.label}</span>
                          {v.is_final && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-medium">FINAL</span>}
                          <span className="text-xs text-zinc-500 truncate flex-1">{v.notes || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <ListSentinel hasMore={hasMore} sentinelRef={sentinelRef} />

      <EditItemDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} kind="song" item={editing} onSaved={load} />
    </div>
  );
}
